import { randomId } from "./random-id";
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

export type UploadReceipt = { id: string; owner: string; objectKey: string; contentType: string; state: 'queued' | 'uploading' | 'completed' | 'failed' | 'cancelled'; progress: number };
const bridge = registerPlugin<{
  start(options: { id: string; owner: string; objectKey: string; contentType: string; path: string; url: string; headers: Record<string, string> }): Promise<void>;
  status(options: { id: string; owner: string }): Promise<UploadReceipt>;
  acknowledge(options: { id: string; owner: string }): Promise<void>;
  cancel(options: { id: string; owner: string }): Promise<void>;
}>('SeasonUpload');
const accountGenerations = new Map<string, number>();
export const hasNativeUpload = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SeasonUpload');

/** Native files and task state are scoped by account. Signed URLs never enter web storage. */
export async function uploadNative(file: File, upload: { owner: string; objectKey: string; url: string; headers: Record<string, string> }, options: { signal?: AbortSignal; onProgress?: (fraction: number) => void; onQueued?: () => void } = {}) {
  const abortError = () => new DOMException('Upload cancelled', 'AbortError');
  const generation = accountGenerations.get(upload.owner) ?? 0;
  const cancelled = () => options.signal?.aborted || (accountGenerations.get(upload.owner) ?? 0) !== generation;
  if (cancelled()) throw abortError();
  const bytes = new Uint8Array(await file.arrayBuffer());
  const fingerprint = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2, '0')).join('');
  if (cancelled()) throw abortError();
  const recoveryKey = `ll.upload.${upload.owner}.${fingerprint}`;
  let previous: { id: string; objectKey: string; at: number } | undefined;
  try { previous = JSON.parse(localStorage.getItem(recoveryKey) || 'null') ?? undefined; } catch { /* Corrupt receipts cannot authorize a transfer. */ }
  if (previous && Date.now() - previous.at < 7 * 86400000 && typeof previous.objectKey === 'string' && previous.objectKey.startsWith(upload.owner + '/')) {
    let recovered: UploadReceipt | undefined;
    try { recovered = await bridge.status({ id: previous.id, owner: upload.owner }); }
    catch { /* A missing receipt can be retried, but never retry an awaited cancellation. */ }
    if (cancelled() || recovered?.state === 'cancelled') throw abortError();
    if (recovered?.state === 'completed') return recovered.objectKey;
    if (recovered?.state === 'queued' || recovered?.state === 'uploading') { options.onQueued?.(); return await wait(previous.id, recovered.objectKey); }
  }
  const id = randomId();
  const path = `uploads/${upload.owner}/${id}`;
  // Existing limits cap files at 25MB; chunks avoid JS argument/stack overflow.
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  const { uri } = await Filesystem.writeFile({ path, directory: Directory.Data, data: btoa(binary), recursive: true });
  let started = false;
  try {
    if (cancelled()) throw abortError();
    // Persist the correlation before enqueue so process death cannot orphan a successful receipt.
    localStorage.setItem(recoveryKey, JSON.stringify({ id, objectKey: upload.objectKey, at: Date.now() }));
    await bridge.start({ ...upload, id, path: uri, contentType: file.type });
    started = true;
    options.onQueued?.();
    return await wait(id, upload.objectKey);
  } finally {
    // Native owns staged-file cleanup once accepted, including after process restart.
    if (!started) await Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
  }
  async function wait(id: string, objectKey: string): Promise<string> {
    const deadline = Date.now() + 65 * 60 * 1000;
    while (true) {
      if (cancelled()) { await bridge.cancel({ id, owner: upload.owner }).catch(() => {}); throw abortError(); }
      if (Date.now() > deadline) { await bridge.cancel({ id, owner: upload.owner }).catch(() => {}); throw new Error('Upload timed out. Please check your connection and retry.'); }
      const state = await bridge.status({ id, owner: upload.owner });
      if (cancelled()) { await bridge.cancel({ id, owner: upload.owner }).catch(() => {}); throw abortError(); }
      options.onProgress?.(Math.max(0, Math.min(1, state.progress)));
      if (state.state === 'completed') return objectKey;
      if (state.state === 'cancelled') throw abortError();
      if (state.state === 'failed') throw new Error('Upload failed. Your draft is still available. Please retry.');
      await new Promise(resolve => setTimeout(resolve, 400));
    }
  }
}

/** Called on sign-out/account deletion; cannot cancel another account's task. */
export async function cancelAccountUploads(owner: string) {
  accountGenerations.set(owner, (accountGenerations.get(owner) ?? 0) + 1);
  if (!hasNativeUpload()) return;
  const prefix = `ll.upload.${owner}.`;
  for (const key of Object.keys(localStorage).filter(key => key.startsWith(prefix))) {
    try { const { id } = JSON.parse(localStorage.getItem(key) || '{}'); await bridge.cancel({ id, owner }); await bridge.acknowledge({ id, owner }); } catch { /* Already completed/missing. */ }
    localStorage.removeItem(key);
  }
}
